import { Switch, TextInput } from '@mantine/core';
import {
  IconAlertTriangle,
  IconBan,
  IconChevronDown,
  IconChevronRight,
  IconEngine,
  IconEngineFilled,
  IconFolder,
  IconFolderFilled,
  IconSearch,
  IconSettingsFilled,
  IconTool,
} from '@tabler/icons-react';
import {
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { EmptyState } from '../../EmptyState.js';
import type { TreeRow } from './equipment-tree.js';
import classes from './Pms.module.css';
import type { EquipmentNodeType } from './types.js';

/** Düğüm türünün ikonu (`gemi-pms-ekipman.jpeg` ağacı): açık sistem dolu ve mavi. */
export function NodeIcon({
  type,
  expanded = false,
  size = 16,
}: {
  type: EquipmentNodeType;
  expanded?: boolean;
  size?: number;
}) {
  switch (type) {
    case 'group':
      return expanded ? <IconFolderFilled size={size} /> : <IconFolder size={size} stroke={1.8} />;
    case 'system':
      return expanded ? <IconEngineFilled size={size} /> : <IconEngine size={size} stroke={1.8} />;
    case 'equipment':
      return <IconSettingsFilled size={size} />;
    default:
      return <IconTool size={size - 2} stroke={1.8} />;
  }
}

export interface EquipmentTreeProps {
  rows: readonly TreeRow[];
  selectedId: string | null;
  /** Klavye odağının satırı (roving tabindex); yoksa seçili ya da ilk satır. */
  focusedId: string | null;
  query: string;
  onQuery: (query: string) => void;
  onSelect: (id: string) => void;
  onToggle: (id: string) => void;
  onKey: (key: string, id: string) => void;
  onFocus: (id: string) => void;
  /** Ağaç boşken (arama yokken) gösterilecek durum. */
  empty?: ReactNode;
  /**
   * Klavye isteği sayacı: yalnız değişince odak satıra taşınır (ok tuşlarıyla gezinme). Arama
   * kutusuna yazmak ya da üst karttan seçmek odağı ağaca çekmez (M7A-SCR-3).
   */
  focusRequest?: number;
  /** "Critical only" süzgeci (M7c; K22); verilmezse çizilmez. */
  criticalOnly?: boolean;
  onCriticalOnly?: (value: boolean) => void;
  /** PMS dışı düğümler (K25): satırda işaret. */
  outOfPms?: ReadonlySet<string>;
}

const KEYS = new Set([
  'ArrowDown',
  'ArrowUp',
  'ArrowRight',
  'ArrowLeft',
  'Home',
  'End',
  'Enter',
  ' ',
]);

/**
 * "Equipment Tree" kartı: arama ve WAI-ARIA tree (düz liste, `aria-level`; tek sekme durağı).
 * Ok tuşları gezinir, → ve ← açar/kapatır, Enter ve boşluk seçer; satıra tıklamak seçer, oka
 * tıklamak açar/kapatır. Uzun ad üç noktayla kısalır, tamamı ipucundadır.
 */
export function EquipmentTree({
  rows,
  selectedId,
  focusedId,
  query,
  onQuery,
  onSelect,
  onToggle,
  onKey,
  onFocus,
  empty,
  focusRequest = 0,
  criticalOnly = false,
  onCriticalOnly,
  outOfPms,
}: EquipmentTreeProps) {
  const { t } = useTranslation();
  const k = (key: string, options: Record<string, unknown> = {}) =>
    t(`pms.equipment.tree.${key}`, options);
  const listRef = useRef<HTMLDivElement>(null);
  // İşaretlerin açıklaması ağacın dışında: satırın adı ve metni değişmez (ekran okuyucu ayrıca okur)
  const flagId = useId();
  const tabStop =
    rows.find((r) => r.node.id === focusedId)?.node.id ??
    rows.find((r) => r.node.id === selectedId)?.node.id ??
    rows[0]?.node.id ??
    null;

  // Odak yalnız klavye isteğiyle taşınır; satır süzgeçle değişince değil. Ağaç yeniden takılınca
  // (gemi değişti, yeniden okundu) eski istek tekrar işlenmez (düzeltme denetimi M7A-SCR-3)
  const tabStopRef = useRef(tabStop);
  tabStopRef.current = tabStop;
  const handledRequest = useRef(focusRequest);
  useEffect(() => {
    if (focusRequest === handledRequest.current) return;
    handledRequest.current = focusRequest;
    if (!tabStopRef.current) return;
    const item = listRef.current?.querySelector<HTMLElement>(
      `[data-node-id="${tabStopRef.current}"]`,
    );
    if (item && item !== document.activeElement) item.focus();
  }, [focusRequest]);

  const keyDown = (event: KeyboardEvent<HTMLDivElement>, id: string) => {
    if (!KEYS.has(event.key)) return;
    event.preventDefault();
    onKey(event.key, id);
  };

  return (
    <div className={classes.tree}>
      <TextInput
        className={classes.treeSearch}
        placeholder={k('search')}
        aria-label={k('searchLabel')}
        leftSection={<IconSearch size={15} aria-hidden />}
        value={query}
        onChange={(event) => onQuery(event.currentTarget.value)}
      />
      <span id={`${flagId}-critical`} className={classes.srOnly}>
        {t('pms.condition.critical.marker')}
      </span>
      <span id={`${flagId}-out`} className={classes.srOnly}>
        {t('pms.condition.critical.outOfPms')}
      </span>
      {onCriticalOnly ? (
        <Switch
          size="xs"
          className={classes.treeFilter}
          classNames={{ trackLabel: classes.switchNoLabel }}
          label={t('pms.condition.critical.only')}
          checked={criticalOnly}
          onChange={(event) => onCriticalOnly(event.currentTarget.checked)}
        />
      ) : null}
      {rows.length === 0 ? (
        criticalOnly ? (
          <EmptyState title={t('pms.condition.critical.none')} />
        ) : query.trim() !== '' ? (
          <EmptyState title={k('noMatch', { query: query.trim() })} />
        ) : (
          (empty ?? <EmptyState title={k('empty')} />)
        )
      ) : (
        <div role="tree" aria-label={k('label')} className={classes.treeList} ref={listRef}>
          {rows.map((row) => {
            const { node } = row;
            const selected = node.id === selectedId;
            return (
              <div
                key={node.id}
                role="treeitem"
                aria-level={row.level}
                aria-setsize={row.setSize}
                aria-posinset={row.position}
                aria-selected={selected}
                aria-expanded={row.hasChildren ? row.expanded : undefined}
                tabIndex={node.id === tabStop ? 0 : -1}
                data-node-id={node.id}
                data-node-type={node.nodeType}
                aria-describedby={
                  [
                    node.critical ? `${flagId}-critical` : null,
                    outOfPms?.has(node.id) ? `${flagId}-out` : null,
                  ]
                    .filter(Boolean)
                    .join(' ') || undefined
                }
                data-selected={selected || undefined}
                data-match={row.match || undefined}
                className={classes.treeItem}
                style={{ '--tree-level': row.level - 1 } as CSSProperties}
                onClick={(event) => {
                  const toggle = (event.target as HTMLElement).closest('[data-toggle]');
                  if (toggle && row.hasChildren) onToggle(node.id);
                  else onSelect(node.id);
                }}
                onKeyDown={(event) => keyDown(event, node.id)}
                onFocus={() => onFocus(node.id)}
              >
                {Array.from({ length: row.level - 1 }, (_, rail) => rail).map((rail) => (
                  <span
                    key={rail}
                    className={classes.treeRail}
                    style={{ '--rail': rail } as CSSProperties}
                    aria-hidden
                  />
                ))}
                <span className={classes.treeToggle} data-toggle aria-hidden>
                  {row.hasChildren ? (
                    row.expanded ? (
                      <IconChevronDown size={14} stroke={2} />
                    ) : (
                      <IconChevronRight size={14} stroke={2} />
                    )
                  ) : row.level > 1 ? (
                    <span className={classes.treeElbow} />
                  ) : null}
                </span>
                <span
                  className={classes.treeIcon}
                  data-expanded={row.expanded || undefined}
                  aria-hidden
                >
                  <NodeIcon type={node.nodeType} expanded={row.expanded} />
                </span>
                <span className={classes.treeName} title={`${node.name} · ${node.code}`}>
                  {node.name}
                </span>
                {/* Kritik işaretli düğüm (K22) ve PMS dışı (K25): ikon ve ekran okuyucu metni */}
                {node.critical ? (
                  <span
                    className={classes.treeFlag}
                    data-flag="critical"
                    title={t('pms.condition.critical.marker')}
                    aria-hidden
                  >
                    <IconAlertTriangle size={13} stroke={2.2} aria-hidden />
                  </span>
                ) : null}
                {outOfPms?.has(node.id) ? (
                  <span
                    className={classes.treeFlag}
                    data-flag="out"
                    title={t('pms.condition.critical.outOfPms')}
                    aria-hidden
                  >
                    <IconBan size={13} stroke={2.2} aria-hidden />
                  </span>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
